import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Store, AlertCircle, CheckCircle, ArrowRight, 
  Package, Users, BarChart3, Zap, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopifyInstallPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [shopDomain, setShopDomain] = useState('');
  const [installing, setInstalling] = useState(false);

  const error = searchParams.get('error');
  const shop = searchParams.get('shop');

  useEffect(() => {
    if (shop) {
      setShopDomain(shop);
    }
  }, [shop]);

  const handleInstall = () => {
    if (!shopDomain) return;

    setInstalling(true);
    
    // Clean shop domain
    let cleanDomain = shopDomain.trim().toLowerCase();
    if (!cleanDomain.includes('.myshopify.com')) {
      cleanDomain = `${cleanDomain}.myshopify.com`;
    }

    // Redirect to install endpoint
    window.location.href = `${API_BASE_URL}/api/shopify-billing/install?shop=${cleanDomain}`;
  };

  const features = [
    {
      icon: Package,
      title: 'Order Management',
      description: 'Sync orders automatically from Shopify'
    },
    {
      icon: BarChart3,
      title: 'Inventory Control',
      description: 'Real-time stock tracking across locations'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Role-based access for your team'
    },
    {
      icon: Zap,
      title: 'Automation',
      description: 'Automated workflows and notifications'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 mb-6 shadow-lg shadow-purple-500/30">
              <Store className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Connect Your Shopify Store
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Get started with Opswarex in seconds. We'll automatically sync your products, orders, and customers.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-red-300">
                {error.replace(/\+/g, ' ')}
              </p>
            </div>
          )}

          {/* Install Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-12 border border-white/20">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your Shopify Store URL
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={shopDomain}
                    onChange={(e) => setShopDomain(e.target.value)}
                    placeholder="your-store"
                    className="flex-1 px-4 py-3 rounded-l-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <span className="px-4 py-3 bg-white/5 border border-l-0 border-white/20 rounded-r-lg text-gray-400">
                    .myshopify.com
                  </span>
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleInstall}
                  disabled={!shopDomain || installing}
                  size="lg"
                  className="w-full md:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-lg px-8"
                >
                  {installing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      Install App
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10 hover:border-purple-500/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* What happens next */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-8 border border-white/10">
            <h2 className="text-xl font-semibold text-white mb-6 text-center">
              What happens next?
            </h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">
                  1
                </div>
                <span className="text-gray-300">Authorize Opswarex</span>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 hidden md:block" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">
                  2
                </div>
                <span className="text-gray-300">Auto-create account</span>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 hidden md:block" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">
                  3
                </div>
                <span className="text-gray-300">Sync data</span>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-500 hidden md:block" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-gray-300">Ready to go!</span>
              </div>
            </div>
          </div>

          {/* Already have account */}
          <div className="text-center mt-8">
            <p className="text-gray-400">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-purple-400 hover:text-purple-300 underline"
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
